import React, { useCallback, useState } from 'react'
import { Col, Container, Row, Spinner } from 'react-bootstrap'
import { useAppSelector } from '../../../../../store/hooks'
import { selectRoomSession, selectRoomSessionIsLoading } from '../../../../../store/meetingSummary/roomSession'
import { useMeetingUsers } from '../hooks/users'
import { ExternalUserServiceIntegration, Me, User } from '../../../../../models/User'
import { NavLink } from 'react-router-dom'
import buttonStyles from '../../../../../components/Button/Button.module.scss'
import { getUserDisplayName } from '../../common/aifeatures/utils'
import { UserTag } from '../../UserTag'
import { selectAccountId, selectSafeAuthenticatedUser } from '../../../../../store/auth/authSlice'
import { Button } from '../../../../../components/Button/Button'
import { AddParticipantModal } from './AddParticipantModal'
import { useRemoveAllocationContextUserMutation } from '../../../../../store/media/media'
import { ScopedBlock } from '../../../../../components/ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../../../../../components/ScopedBlock/models'
import { CONTACT_UPDATE_SUPPORTED_INTEGRATIONS, getIntegrationDisplayName, IntegrationServiceId } from '../../../../../common/integrations'
import { Tooltip } from '../../../../../components/Tooltip/Tooltip'
import './Participants.scss'

import { useGetMeQuery } from '../../../../../store/user/user'
import { LinkContactModal } from './LinkContactModal'
import { IntegrationIcon } from '../../../../../components/IntegrationIcon'
import { TrackEvent, useTracking } from '../../common/tracking'

export interface ParticipantsProps {
  interactionsLoading: boolean
  onContactUpdated?: (serviceId: string) => void
}

const StyledIntegrationIcon = ({ serviceId }: { serviceId: string }) => {
  return (
    <>
      <IntegrationIcon serviceId={serviceId as IntegrationServiceId} fill='var(--zocks-gray-900)' width='1rem' height='1rem' />
      <i className='integration-icon-badge bi bi-circle-fill' />
    </>
  )
}

export const Participants: React.FC<ParticipantsProps> = ({ interactionsLoading, onContactUpdated }) => {
  const accountId = useAppSelector(selectAccountId)
  const { id: currentUserId } = useAppSelector(selectSafeAuthenticatedUser)
  const roomSession = useAppSelector(selectRoomSession)
  const { roomId = '', id: sessionId = '' } = roomSession ?? {}
  const roomSessionIsLoading = useAppSelector(selectRoomSessionIsLoading)
  const { usersByRoles, isFetching: isUsersFetching, refetchUsers: refetchContact } = useMeetingUsers(roomSession, ['external'])
  const [showAddParticipantModal, setShowAddParticipantModal] = useState(false)
  const contacts = usersByRoles['Clients'] ?? []
  const { data: currentUserData } = useGetMeQuery({ accountId: accountId }, { refetchOnMountOrArgChange: true })
  const [showLinkContactModal, setShowLinkContactModal] = useState<boolean>(false)
  const [serviceId, setServiceId] = useState<string>('')
  const [user, setUser] = useState<User>()
  const { tracked } = useTracking()

  const handleAddParticipantClicked = () => {
    setShowAddParticipantModal(true)
  }

  const handleParticipantAdded = async () => {
    setShowAddParticipantModal(false)
  }

  const _handleSaveLinkContactModal = useCallback(() => {
    setShowLinkContactModal(false)
    refetchContact()
    if (serviceId) {
      onContactUpdated?.(serviceId)
    }
  }, [refetchContact, onContactUpdated, serviceId])

  const handleSaveLinkContactModal = useCallback(
    () =>
      tracked(_handleSaveLinkContactModal, {
        eventName: TrackEvent.CONTACT_LINKED_TO_INTEGRATION,
        eventDetails: { type: 'manual', source: 'meetingPreparation', serviceId: serviceId, userId: user?.id },
      }),
    [tracked, _handleSaveLinkContactModal, serviceId, user],
  )

  const handleCancelLinkContactModal = () => {
    setShowLinkContactModal(false)
  }

  interface ParticipantProps {
    user: User
    manual: boolean
    currentUserData: Me | undefined
  }

  const handleLinkParticipantClicked = (user: User, serviceId: string) => {
    setServiceId(serviceId)
    setUser(user)
    setShowLinkContactModal(true)
  }

  const LinkParticipantButton: React.FC<{ user: User; serviceId: string; disable: boolean }> = ({ user, serviceId, disable }) => {
    if (user.id.startsWith('speaker')) {
      return (
        <Tooltip
          className='paragraph-small'
          title={'Configure Participant Assignments by clicking the gear icon before creating or updating contact data in connected systems.'}
          placement='top'
          offset={[0, 10]}
        >
          <Button variant='header' className='p-1 btn-header' disabled={true}>
            <StyledIntegrationIcon serviceId={serviceId} />
          </Button>
        </Tooltip>
      )
    }
    const serviceDisplayName = getIntegrationDisplayName(serviceId as IntegrationServiceId)
    return (
      <div className='d-flex'>
        <Tooltip
          className='paragraph-small'
          title={`Link your contact to their profile in ${serviceDisplayName} to pull their client data into Meeting Prep.`}
          placement='top'
          offset={[0, 10]}
        >
          <Button variant='header' className='p-1 btn-header' onClick={() => handleLinkParticipantClicked(user, serviceId)} disabled={disable}>
            <StyledIntegrationIcon serviceId={serviceId} />
          </Button>
        </Tooltip>
      </div>
    )
  }

  const Participant: React.FC<ParticipantProps> = ({ user, manual, currentUserData }) => {
    const { id: userId, type } = user
    const userType = currentUserId === userId ? 'current' : (type ?? 'anonymous')
    const [removeAllocationContextUser, { isLoading: removingAllocationContextUser }] = useRemoveAllocationContextUserMutation()

    const isAnonymous = userType === 'anonymous'
    const isExternal = userType === 'external'

    const handleRemoveParticipant = (userId: string) => {
      removeAllocationContextUser({
        accountId,
        roomId,
        sessionId,
        body: { userId },
      })
    }

    const getUnlinkedIntegrations = (): ExternalUserServiceIntegration[] => {
      if (!currentUserData?.integrations) {
        return []
      }

      const currentUserIntegrations = currentUserData.integrations

      const linkedServiceIds = user.integrations ? new Set(user.integrations.map((link) => link.serviceId)) : new Set()

      return currentUserIntegrations
        .filter((integration) => !linkedServiceIds.has(integration.serviceId))
        .filter((integration) => CONTACT_UPDATE_SUPPORTED_INTEGRATIONS.includes(integration.serviceId as IntegrationServiceId))
    }

    return (
      <div className='assignment-border me-auto'>
        <Container className='mx-0 participant-container'>
          <Row className='px-3 py-2 d-flex justify-content-between align-items-center'>
            <Col className='p-0'>
              <Container className='p-0'>
                <Row className='participant-name-container p-0 d-flex gap-2 mx-0'>
                  <Col className='p-0 d-flex align-items-center gap-2' xs='auto'>
                    <div className='assignment-person-icon'>
                      <i className='bi bi-person-fill' />
                    </div>
                    {type === 'external' ? (
                      <NavLink
                        className={`${buttonStyles.button} ${buttonStyles.linkButton} button-link-medium`}
                        to={`/library/manage-contacts/contacts/${userId}`}
                        target='_blank'
                      >
                        <div className='paragraph-medium bold'>{getUserDisplayName(user)}</div>
                      </NavLink>
                    ) : (
                      <div className='paragraph-medium bold'>{getUserDisplayName(user)}</div>
                    )}
                  </Col>
                  <Col className='p-0 d-flex align-items-center gap-2' xs='auto'>
                    <UserTag className='zocks-py-0.125 px-2' userType={userType} />
                    {(isAnonymous || isExternal) && getUnlinkedIntegrations().length > 0
                      ? getUnlinkedIntegrations().map(({ serviceId }) => (
                          <LinkParticipantButton key={serviceId} user={user} serviceId={serviceId} disable={interactionsLoading} />
                        ))
                      : null}
                  </Col>
                </Row>
              </Container>
            </Col>
            {manual ? (
              <Col xs='auto'>
                <ScopedBlock
                  scope={{
                    scope: 'allocation_context:create',
                    superScope: ['allocation_context:admin'],
                  }}
                  missingScopeAction={MissingScopeAction.DISABLE}
                >
                  <Button
                    variant='header'
                    loading={removingAllocationContextUser}
                    onClick={() => handleRemoveParticipant(user.id)}
                    disabled={removingAllocationContextUser}
                  >
                    <i className='bi bi-trash3 d-flex align-items-center' />
                  </Button>
                </ScopedBlock>
              </Col>
            ) : null}
          </Row>
        </Container>
      </div>
    )
  }

  return (
    <>
      {showAddParticipantModal && (
        <AddParticipantModal
          show={showAddParticipantModal}
          existingClients={contacts}
          onCancel={() => setShowAddParticipantModal(false)}
          onSuccess={handleParticipantAdded}
        />
      )}
      {user && showLinkContactModal ? (
        <LinkContactModal
          show={showLinkContactModal}
          onCancel={handleCancelLinkContactModal}
          onSave={handleSaveLinkContactModal}
          serviceId={serviceId}
          user={user}
        />
      ) : null}
      {contacts.length ? (
        <Container className='d-flex flex-column mx-0 w-100 mw-100 px-3 py-4 gap-3'>
          <Row className='m-0 d-flex gap-4'>
            <Col className='d-flex gap-2 align-items-center p-0' xs='auto'>
              <h5>Participants</h5>
            </Col>
            <Col className='p-0' xs='auto'>
              <ScopedBlock
                scope={{
                  scope: 'allocation_context:create',
                  superScope: ['allocation_context:admin'],
                }}
                missingScopeAction={MissingScopeAction.DISABLE}
              >
                <Button variant='header' type='button' onClick={handleAddParticipantClicked} disabled={roomSessionIsLoading}>
                  <i className='bi bi-plus-circle' />
                  Add Client
                </Button>
              </ScopedBlock>
            </Col>
          </Row>
          <Row className='m-0'>
            <Col className='p-0 gap-3'>
              {isUsersFetching ? (
                <div className='d-flex align-items-center justify-content-start'>
                  <Spinner />
                </div>
              ) : (
                <>
                  <Row className='m-0'>Clients ({contacts.length})</Row>
                  <div className='d-flex flex-column gap-1 participants-container'>
                    {contacts.map(({ user, manual }) => {
                      return <Participant key={user.id} user={user} manual={manual} currentUserData={currentUserData} />
                    })}
                  </div>
                </>
              )}
            </Col>
          </Row>
        </Container>
      ) : null}
    </>
  )
}
